import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRememberedState, useRememberedScroll } from '../../hooks/useRemembered';
import { Customer, InvoiceConfig, Quote, QuoteStatus, QuoteConfig, QuoteTemplate } from '../../types';
import {
  fetchQuotes, fetchCustomers, createQuote, deleteQuote, updateQuote,
  snoozeFollowUp, recordFollowUp,
  fetchQuoteTemplates, deleteQuoteTemplate, duplicateQuote,
} from '../../services/crmService';
import { computeFollowUpDate, quoteAmountSuffix } from '../../services/quoteLogic';
import { applyTemplateToCreateInput, splitCreateQuoteFields } from '../../services/quoteCreateInput';
import { getQuoteEditorBusy, setQuoteEditorBusy } from '../../services/editorBusy';
import { clearQuoteDraftBackup } from '../../services/quoteDraftBackup';
import { TypeDigit } from '../../services/documentNumberService';
import { computeQuoteStats, effectiveQuoteStatus, periodWindow, QuotePeriod } from '../../services/quoteStats';
import { QuoteKpiBar } from './QuoteKpiBar';
import { Button } from '../Button';
import {
  FileText, Plus, Loader2, AlertTriangle, RefreshCw, Trash2, X, Bell,
  Building2, Copy, Bookmark, Layers, Search, Upload, FolderOpen, UserPlus,
} from 'lucide-react';
import { CustomerPicker } from './CustomerPicker';
import { NewCustomerDialog } from './NewCustomerDialog';
import { useVertriebRechte } from './useVertriebRechte';
import { QuoteFollowUpTab } from './QuoteFollowUpTab';
import { QuoteFollowUpDoneModal } from './QuoteFollowUpDoneModal';
import { FollowUpInput, followUpDueState } from '../../services/quoteFollowUp';
import { buildActorName, realActorName } from '../../services/actorName';
import { QuoteEditor, type OpenProjectTarget } from './QuoteEditor';
import { QuoteDocumentImportModal, QuoteDocumentImportResult } from './QuoteDocumentImportModal';
import { buildImportedQuoteUpdates, isImportedQuote } from '../../services/quoteDocumentImport';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { mergeCustomerIntoList } from '../../services/customerQuickEdit';
import { quoteOrderSummary } from '../../services/quoteOrderTranches';
import { isSupplementQuote, supplementLabel } from '../../services/quoteSupplementLogic';
import {
  createSupplementQuote, searchSupplementProjects, type SupplementProjectOption,
} from '../../services/quoteSupplementService';
import { SUPPLEMENT_SEARCH_DELAY_MS, supplementPickState } from './supplementQuoteEntry';

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const fmtEur = (n: number) => eur.format(Number.isFinite(n) ? n : 0);
const fmtDate = (v?: string) => (v ? new Date(v).toLocaleDateString('de-DE') : '—');
const todayIso = () => new Date().toISOString().slice(0, 10);

// Effektiver Status: 'sent' + abgelaufen -> als 'expired' anzeigen.
// Gemeinsame Quelle mit den KPI-Kacheln, damit Liste und Kennzahlen nie
// unterschiedlich zaehlen.
const effectiveStatus = (q: Quote): QuoteStatus => effectiveQuoteStatus(q, todayIso());

// Zeitraum der KPI-Leiste ueberlebt den Seitenwechsel.
const PERIOD_KEY = 'crm.quoteKpiPeriod';
const loadPeriod = (): QuotePeriod => {
  try {
    const v = localStorage.getItem(PERIOD_KEY);
    if (v === '90d' || v === 'year' || v === 'all') return v;
  } catch {
    // Privater Modus / kein Storage -> Default.
  }
  return 'year';
};

const STATUS_META: Record<QuoteStatus, { label: string; cls: string }> = {
  draft: { label: 'Entwurf', cls: 'bg-slate-100 text-slate-600' },
  sent: { label: 'Versendet', cls: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Angenommen', cls: 'bg-emerald-100 text-emerald-700' },
  partial: { label: 'Teilbeauftragt', cls: 'bg-teal-100 text-teal-700' },
  rejected: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-700' },
  expired: { label: 'Abgelaufen', cls: 'bg-amber-100 text-amber-700' },
};

const FILTERS: { key: QuoteStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'Alle' },
  { key: 'draft', label: 'Entwurf' },
  { key: 'sent', label: 'Versendet' },
  { key: 'accepted', label: 'Angenommen' },
  { key: 'partial', label: 'Teilbeauftragt' },
  { key: 'rejected', label: 'Abgelehnt' },
  { key: 'expired', label: 'Abgelaufen' },
];

const StatusBadge: React.FC<{ status: QuoteStatus }> = ({ status }) => {
  const m = STATUS_META[status];
  return <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ${m.cls}`}>{m.label}</span>;
};

interface QuotesViewProps {
  // Angebots-Voreinstellungen aus AdminGlobalSettings (profiles.settings.quoteConfig).
  quoteConfig?: QuoteConfig;
  // Rechnungs-Stammdaten (aufgeloest via resolveInvoiceConfig) — speisen die
  // Spalten-Fusszeile des Angebots-PDFs, damit Angebot und Rechnung denselben
  // Fuss tragen. NICHT aus dem settings-Prop lesbar: das ist ein von App.tsx
  // handgebautes Teilobjekt ohne invoiceConfig.
  invoiceConfig?: InvoiceConfig;
  // Firmenkopf fuer Angebots-PDF + Outlook-Versand (aus userProfile abgeleitet).
  company?: { name?: string; address?: string; logo?: string; footerText?: string; senderLine?: string };
  // Phase 5 (Auftragsannahme): Koordinator-Profil + App-Settings fuer den
  // Projekt-Report, und Sprung ins angelegte Projekt (App.handleOpenProject).
  userProfile?: any;
  settings?: any;
  onOpenProject?: (project: OpenProjectTarget) => void;
  /**
   * Sprung aus der Kundenakte: dieses Angebot nach dem Laden direkt oeffnen.
   * Startwert, wirkt einmal je Aufbau.
   */
  initialQuoteId?: string;
  /** Schliessen DIESES Angebots fuehrt dorthin zurueck, woher gesprungen wurde. */
  onCloseInitialQuote?: () => void;
}

export const QuotesView: React.FC<QuotesViewProps> = ({ quoteConfig, invoiceConfig, company, userProfile, settings, onOpenProject, initialQuoteId, onCloseInitialQuote }) => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  // Reiter der Ansicht. "Nachfassen" ist ein eigener Arbeitsplatz — vorher war
  // es eine Kachel ueber der Liste, in der ein Klick auf "Erledigt" das
  // Angebot ohne jede Aufzeichnung verschwinden liess.
  const [tab, setTab] = useRememberedState<'quotes' | 'followup'>('quotes.tab', 'quotes');
  const scrollRef = useRememberedScroll('quotes.scroll');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filter, setFilter] = useRememberedState<QuoteStatus | 'all'>('quotes.filter', 'all');
  const [searchTerm, setSearchTerm] = useRememberedState('quotes.search', '');
  const [editing, setEditing] = useState<Quote | null>(null);
  // Offener Editor sperrt den automatischen Reload bei neuem Deployment
  // (App.tsx, Audit 24.09. Q5). Ein laufendes Speichern bleibt beim Schliessen
  // gemeldet — das nimmt der Editor selbst zurueck.
  const editorOpen = editing !== null;
  useEffect(() => {
    if (!editorOpen) return;
    setQuoteEditorBusy(true, getQuoteEditorBusy().saving);
    return () => setQuoteEditorBusy(false, getQuoteEditorBusy().saving);
  }, [editorOpen]);

  // KPI-Leiste: Zeitraum der Erfolgs-Kennzahlen. Die Nachfass-Kachel springt
  // in den Reiter.
  const [period, setPeriod] = useState<QuotePeriod>(loadPeriod);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerStep, setPickerStep] = useState<'customer' | 'template' | 'project'>('customer');
  const [pickedCustomer, setPickedCustomer] = useState<Customer | null>(null);
  // Art des neuen Angebots: frei (weiter zu den Vorlagen) oder Nachtrag zu
  // einem bestehenden Projekt (weiter zur Projektauswahl).
  const [pickedKind, setPickedKind] = useState<'new' | 'supplement'>('new');
  const [projectQuery, setProjectQuery] = useState('');
  const [projectOptions, setProjectOptions] = useState<SupplementProjectOption[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectSearchError, setProjectSearchError] = useState<string | null>(null);
  // Anlegen des Nachtrags gescheitert — steht im Dialog, der offen bleibt.
  const [pickerError, setPickerError] = useState<string | null>(null);
  // Typziffer des Nummernkreises. Muss HIER stehen: die Angebotsnummer
  // (A-JJMMTNNN) wird beim Anlegen gezogen und ist danach unveraenderlich.
  const [pickedTypeDigit, setPickedTypeDigit] = useState<TypeDigit>('1');
  const [creating, setCreating] = useState(false);
  // Kunde fehlt noch im Stamm: Neuanlage direkt aus Schritt 1 (null = zu,
  // sonst die Vorbelegung des Namens). Nur mit „Kunden verwalten“ — die
  // Datenbank laesst `customers` anlegen sonst nicht zu.
  const darf = useVertriebRechte();
  const [newCustomerName, setNewCustomerName] = useState<string | null>(null);

  // Angebot einlesen (Alt-/Fremdbeleg als PDF).
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  // Vorlagen (Schritt 2 im "Neues Angebot"-Flow).
  const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);

  // "Wiederverwenden": Angebot fuer einen (anderen) Kunden duplizieren.
  const [reuseTarget, setReuseTarget] = useState<Quote | null>(null);
  const [reuseCustomer, setReuseCustomer] = useState<Customer | null>(null);
  const [duplicating, setDuplicating] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Quote | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Angebot, fuer das gerade ein erledigtes Nachfassen erfasst wird.
  const [followUpTarget, setFollowUpTarget] = useState<Quote | null>(null);
  const [savingFollowUp, setSavingFollowUp] = useState(false);

  // Sprungziel aus der Kundenakte: einmal oeffnen (pending), und solange es
  // offen ist, fuehrt Zurueck in die Akte (rueckweg).
  const pendingQuoteIdRef = useRef<string | null>(initialQuoteId ?? null);
  const rueckwegQuoteIdRef = useRef<string | null>(initialQuoteId ?? null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [qs, cs] = await Promise.all([fetchQuotes(), fetchCustomers()]);
      setQuotes(qs);
      setCustomers(cs);
      const sprungId = pendingQuoteIdRef.current;
      pendingQuoteIdRef.current = null;
      if (sprungId) {
        const sprung = qs.find((q) => q.id === sprungId);
        // Nicht (mehr) da: Liste zeigen, und Zurueck bleibt in der Liste.
        if (sprung) setEditing(sprung);
        else rueckwegQuoteIdRef.current = null;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Angebote konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Projektauswahl „Nachtrag zu …": entprellt suchen; eine überholte Antwort
  // (inzwischen weitergetippt oder Dialog zu) wird verworfen.
  const pickedCustomerId = pickedCustomer?.id;
  const projectStepOpen = pickerOpen && pickerStep === 'project';
  useEffect(() => {
    if (!projectStepOpen) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      setProjectsLoading(true);
      searchSupplementProjects(projectQuery.trim(), pickedCustomerId)
        .then((opts) => {
          if (cancelled) return;
          setProjectOptions(opts);
          setProjectSearchError(null);
        })
        .catch((e) => {
          if (cancelled) return;
          setProjectOptions([]);
          setProjectSearchError('Projekte konnten nicht geladen werden: ' + rechtFehlerOder(e));
        })
        .finally(() => {
          if (!cancelled) setProjectsLoading(false);
        });
    }, SUPPLEMENT_SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [projectStepOpen, projectQuery, pickedCustomerId]);

  // Der geladene Kunde gewinnt vor dem am Angebot mitgelesenen Join-Namen
  // (crmService mapQuoteRow). Umgekehrt zeigte die Liste nach einer Umbenennung
  // aus dem Editor heraus weiter den alten Namen — die Kopfzeile und das PDF
  // daneben schon den neuen —, und das Speichern sah gescheitert aus. Der
  // Join-Name bleibt der Rueckfall fuer archivierte/nicht geladene Kunden.
  const customerName = (q: Quote) =>
    customers.find((c) => c.id === q.customerId)?.name || q.customerName || '—';

  const filtered = useMemo(() => {
    const base = filter === 'all' ? quotes : quotes.filter((q) => effectiveStatus(q) === filter);
    const s = searchTerm.trim().toLowerCase();
    if (!s) return base;
    return base.filter((q) =>
      (q.quoteNumber || '').toLowerCase().includes(s) ||
      (q.orderNumber || '').toLowerCase().includes(s) ||
      (customerName(q) || '').toLowerCase().includes(s) ||
      (q.title || '').toLowerCase().includes(s)
    );
    // `customers` gehoert dazu, seit die Namensaufloesung zuerst dort nachsieht:
    // sonst suchte die Liste nach einer Umbenennung weiter nach dem alten Namen.
  }, [quotes, customers, filter, searchTerm]);

  // Kennzahlen immer aus dem vollen Bestand – nicht aus `filtered`, sonst
  // wuerde die Leiste den eigenen Filter messen.
  const stats = useMemo(
    () => computeQuoteStats(quotes, { today: todayIso(), ...periodWindow(period, todayIso()) }),
    [quotes, period],
  );

  // Zaehler des Reiters: faellig faerbt, terminiert zeigt nur den Bestand.
  // Gleiche Bedingung wie die KPI-Kachel (followUpDueState), damit Kachel und
  // Reiter nie verschieden zaehlen.
  const { openFollowUpCount, dueFollowUpCount } = useMemo(() => {
    let open = 0, due = 0;
    for (const q of quotes) {
      const state = followUpDueState(q, todayIso());
      if (state === 'none') continue;
      open += 1;
      if (state !== 'upcoming') due += 1;
    }
    return { openFollowUpCount: open, dueFollowUpCount: due };
  }, [quotes]);

  const handlePeriodChange = (p: QuotePeriod) => {
    setPeriod(p);
    try {
      localStorage.setItem(PERIOD_KEY, p);
    } catch {
      // Nicht speicherbar ist kein Fehler – der Zeitraum gilt dann nur fuer diese Sitzung.
    }
  };

  const showFollowUpTab = () => setTab('followup');

  // ─── Neues Angebot ─────────────────────────────────────────────────────────
  const closePicker = () => {
    setPickerOpen(false);
    setPickerStep('customer');
    setPickedCustomer(null);
    setPickedTypeDigit('1');
    setPickedKind('new');
    setProjectQuery('');
    setProjectOptions([]);
    setProjectsLoading(false);
    setProjectSearchError(null);
    setPickerError(null);
    setNewCustomerName(null);
  };

  // Neu angelegter (oder im Dublettendialog gewaehlter) Kunde: in die Liste
  // und gleich ausgewaehlt — vorher hiess das Angebot abbrechen, im CRM
  // anlegen und von vorn beginnen.
  const takeNewCustomer = (c: Customer) => {
    setCustomers((prev) => mergeCustomerIntoList(prev, c));
    setPickedCustomer(c);
    setNewCustomerName(null);
  };

  // Schritt 1 -> 2: je nach Art Vorlagen oder Projektauswahl.
  const advanceFromCustomerStep = () => {
    if (!pickedCustomer) return;
    if (pickedKind === 'new') {
      void advanceToTemplateStep();
      return;
    }
    setPickerError(null);
    setProjectSearchError(null);
    // Ladeanzeige sofort — die erste Suche laeuft erst nach der Entprellung.
    setProjectsLoading(true);
    setPickerStep('project');
  };

  // Schritt 2 (Nachtrag): leeren Nachtrag zum Projekt anlegen und Editor
  // oeffnen. Nummernkreis, Texte und Hauptangebots-Bezug setzt der Dienst.
  const handleCreateSupplement = async (option: SupplementProjectOption) => {
    if (!pickedCustomer || creating) return;
    if (!supplementPickState(option, pickedCustomer.id).selectable) return;
    setCreating(true);
    setPickerError(null);
    try {
      const result = await createSupplementQuote({
        projectId: option.id,
        customerId: pickedCustomer.id,
        validUntil: computeFollowUpDate(new Date().toISOString(), quoteConfig?.defaultValidityDays ?? 30),
        taxRate: quoteConfig?.defaultTaxRate ?? 19,
        paymentTerms: quoteConfig?.defaultPaymentTerms || undefined,
      });
      setQuotes((prev) => [result, ...prev]);
      closePicker();
      setEditing(result);
    } catch (e) {
      // Klartext des Dienstes (Projekt ohne Kunde, archiviert, Tochter) —
      // im Dialog, damit gleich ein anderes Projekt gewaehlt werden kann.
      setPickerError('Nachtragsangebot konnte nicht angelegt werden: ' + rechtFehlerOder(e));
    } finally {
      setCreating(false);
    }
  };

  // Schritt 1 -> 2: Kunde gewaehlt, Vorlagen laden.
  const advanceToTemplateStep = async () => {
    if (!pickedCustomer) return;
    setPickerStep('template');
    setTemplatesLoading(true);
    try {
      const tpls = await fetchQuoteTemplates();
      setTemplates(tpls);
    } catch {
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Schritt 2: Angebot anlegen (optional mit Vorlage) und Editor oeffnen.
  const handleCreate = async (template: QuoteTemplate | null) => {
    if (!pickedCustomer) return;
    setCreating(true);
    try {
      // Voreinstellungen aus quoteConfig uebernehmen (Fallbacks: 19 %, 30 Tage, ohne Zahlungsziel).
      const validityDays = quoteConfig?.defaultValidityDays ?? 30;
      // Standard-Rahmenvertrag des Kunden vorbelegen: isDefault, sonst der einzige
      // vorhandene, sonst keiner. Format = frameworkLabel im QuoteEditor.
      const fcs = pickedCustomer.frameworkContracts || [];
      const defaultFc = fcs.find((f) => f.isDefault) || (fcs.length === 1 ? fcs[0] : null);
      const frameworkRef = defaultFc
        ? `${defaultFc.title ? defaultFc.title + ' – ' : ''}${defaultFc.number}`
        : undefined;
      const { input, totals } = applyTemplateToCreateInput({
        customerId: pickedCustomer.id,
        typeDigit: pickedTypeDigit,
        title: template?.payload.title || '',
        taxRate: template?.payload.taxRate ?? quoteConfig?.defaultTaxRate ?? 19,
        validUntil: computeFollowUpDate(new Date().toISOString(), validityDays),
        paymentTerms: template?.payload.paymentTerms || quoteConfig?.defaultPaymentTerms || undefined,
        // sections bewusst explizit leer: neue Angebote starten blanko. Ohne
        // das Feld greift im Editor der Fallback auf DEFAULT_QUOTE_SECTIONS,
        // der nur noch fuer Altangebote gilt (deren Positionen verweisen auf
        // die Sektions-IDs planung/bau/stunden).
        details: { sections: [], ...(frameworkRef ? { frameworkRef } : {}) },
      }, template);
      // EIN Insert mit Positionen/Texten/Details der Vorlage (Audit Q7): kein
      // leeres nummeriertes Angebot mehr, wenn ein zweiter Schritt scheitert.
      // Die Summen der Vorlage gehen im selben Insert mit.
      const result = await createQuote(totals ? { ...input, ...totals } : input);

      setQuotes((prev) => [result, ...prev]);
      closePicker();
      setEditing(result);
    } catch (e) {
      alert('Angebot konnte nicht angelegt werden: ' + rechtFehlerOder(e));
    } finally {
      setCreating(false);
    }
  };

  // ─── Angebot einlesen (Alt-/Fremdbeleg als PDF) ────────────────────────────
  // Der Beleg wird zu einem vollwertigen Angebot mit EIGENER Nummer: die
  // Belegnummer des Originals bleibt nur als Herkunft stehen (details.
  // importedQuote), damit sich die Nummernkreise nicht vermischen.
  const handleImportQuote = async (r: QuoteDocumentImportResult) => {
    setImporting(true);
    setImportError(null);
    try {
      const updates = buildImportedQuoteUpdates(r, new Date().toISOString(), { sections: [] });
      const { create, rest } = splitCreateQuoteFields(updates);
      // EIN Insert mit Positionen und Details des eingelesenen Belegs (Audit
      // Q7) — scheitert danach der Nachtrag, bleibt der Import erhalten.
      const created = await createQuote({
        customerId: r.customerId,
        typeDigit: '1',
        title: r.title,
        taxRate: r.taxRate,
        ...create,
      });
      setQuotes((prev) => [created, ...prev]);

      // Status, Datumsangaben und Summen nimmt createQuote nicht an.
      try {
        if (Object.keys(rest).length > 0) await updateQuote(created.id, rest);
      } catch (e) {
        // Positionen sind gespeichert; offen bleiben nur Status/Daten. Der
        // Dialog bleibt offen und sagt das — ein erneutes Einlesen legte ein
        // zweites Angebot an.
        setImportError(
          `Angebot ${created.quoteNumber || ''} wurde mit allen Positionen als Entwurf angelegt, `
          + 'aber Status/Datum/Summen konnten nicht gespeichert werden: ' + rechtFehlerOder(e)
          + ' — bitte NICHT erneut einlesen, sondern das Angebot in der Liste öffnen und nachtragen.',
        );
        return;
      }
      const result = { ...created, ...rest } as Quote;
      setQuotes((prev) => prev.map((q) => (q.id === result.id ? result : q)));
      setImportOpen(false);
      setEditing(result);
    } catch (e) {
      setImportError('Angebot konnte nicht angelegt werden: ' + rechtFehlerOder(e));
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    setDeletingTemplateId(id);
    try {
      await deleteQuoteTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      alert('Vorlage konnte nicht gelöscht werden: ' + rechtFehlerOder(e));
    } finally {
      setDeletingTemplateId(null);
    }
  };

  // ─── Wiederverwenden (Angebot fuer Kunden duplizieren) ──────────────────────
  const handleReuse = async () => {
    if (!reuseTarget || !reuseCustomer) return;
    setDuplicating(true);
    try {
      const draft = await duplicateQuote(reuseTarget, reuseCustomer.id);
      setQuotes((prev) => [draft, ...prev]);
      setReuseTarget(null);
      setReuseCustomer(null);
      setEditing(draft);
    } catch (e) {
      alert('Duplizieren fehlgeschlagen: ' + rechtFehlerOder(e));
    } finally {
      setDuplicating(false);
    }
  };

  const handleQuoteChange = (updated: Quote) => {
    setQuotes((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
    // Kommt nach einem await: der Nutzer kann den Editor inzwischen verlassen
    // oder ein anderes Angebot geoeffnet haben. Nur das noch offene Angebot
    // nachziehen, sonst oeffnete das Speichern A wieder (Audit Q6).
    setEditing((cur) => (cur && cur.id === updated.id ? updated : cur));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteQuote(deleteTarget.id);
      // Eine lokale Entwurfssicherung des geloeschten Angebots bliebe sonst
      // als Waise im Browser liegen (beide Plaetze).
      clearQuoteDraftBackup(deleteTarget.id);
      setQuotes((prev) => prev.filter((q) => q.id !== deleteTarget.id));
      setFollowUpTarget((cur) => (cur && cur.id === deleteTarget.id ? null : cur));
      // Aus dem Editor heraus geloescht: der zeigt sonst ein Angebot an, das
      // es nicht mehr gibt (und der Autosave schriebe es beim naechsten
      // Tastendruck vergeblich fort).
      setEditing((cur) => (cur && cur.id === deleteTarget.id ? null : cur));
      setDeleteTarget(null);
    } catch (e) {
      alert('Löschen fehlgeschlagen: ' + rechtFehlerOder(e));
    } finally {
      setDeleting(false);
    }
  };

  // ─── Nachfässe ─────────────────────────────────────────────────────────────
  // Wer nachfasst: der angemeldete Nutzer. buildActorName laesst nie ein
  // Truemmerstueck ("Max undefined") stehen — im Verlauf soll ein echter Name
  // oder gar keiner stehen.
  const actorName =
    buildActorName(userProfile) || realActorName(userProfile?.email) || '';

  // Erledigtes Nachfassen: Stand, Nachfassender und naechster Termin in EINEM
  // Schreibvorgang. Ohne neuen Termin ist das Nachfassen abgeschlossen.
  const handleFollowUpSubmit = async (input: FollowUpInput) => {
    if (!followUpTarget) return;
    const id = followUpTarget.id;
    setSavingFollowUp(true);
    try {
      const res = await recordFollowUp(id, input);
      const now = new Date().toISOString();
      setQuotes((prev) =>
        prev.map((q) =>
          q.id === id
            ? {
                ...q,
                followUpDate: res.followUpDate,
                followUpDoneAt: res.followUpDoneAt,
                // Neuer Zyklus: die Glocke darf wieder melden (Server hat den
                // Zeitstempel geleert) — der lokale Stand muss mitziehen.
                followUpNotifiedAt: res.followUpDoneAt ? q.followUpNotifiedAt : undefined,
                details: { ...(q.details || {}), followUpLog: [...(q.details?.followUpLog || []), res.entry] },
                updatedAt: now,
              }
            : q,
        ),
      );
      setFollowUpTarget(null);
    } catch (e) {
      alert('Nachfassen konnte nicht gespeichert werden: ' + rechtFehlerOder(e));
    } finally {
      setSavingFollowUp(false);
    }
  };

  const handleSnooze = async (id: string, newDate: string) => {
    if (!newDate) return;
    try {
      await snoozeFollowUp(id, newDate);
      setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, followUpDate: newDate } : q)));
    } catch (e) {
      alert('Verschieben fehlgeschlagen: ' + rechtFehlerOder(e));
    }
  };

  // Der Loeschweg ist bewusst EINER: Rueckfrage, Aufruf und Aufraeumen leben
  // hier, die Liste UND der Editor stossen nur `deleteTarget` an.
  const deleteDialog = deleteTarget ? (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in">
        <div className="flex items-start gap-4 mb-4">
          <div className="bg-red-50 p-3 rounded-full shrink-0"><Trash2 className="w-6 h-6 text-red-600" /></div>
          <div>
            <h3 className="font-bold text-lg text-slate-800">Angebot löschen?</h3>
            <p className="text-sm text-slate-600 mt-1">
              Sie sind dabei, <strong>{deleteTarget.quoteNumber}</strong> zu löschen. Dies kann nicht rückgängig gemacht werden.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>Abbrechen</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting} icon={deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}>
            Löschen
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  // ─── Editor-Ansicht ────────────────────────────────────────────────────────
  if (editing) {
    const cust =
      customers.find((c) => c.id === editing.customerId) ||
      // Fallback-Kunde, falls (noch) nicht in der Liste – nur Name benötigt.
      ({ id: editing.customerId, name: customerName(editing), contacts: [], defaultDiscountPercent: 0, createdBy: '', createdAt: '', updatedAt: '' } as Customer);
    return (
      <>
      <QuoteEditor
        quote={editing}
        customer={cust}
        onChange={handleQuoteChange}
        // Nur die Kundenliste nachziehen — `editing` bleibt unberuehrt, sonst
        // liefe eine reine Stammdatenaenderung als Angebotsaenderung durch.
        onCustomerChange={(updated) => setCustomers((prev) => mergeCustomerIntoList(prev, updated))}
        onBack={() => {
          const zurueck = rueckwegQuoteIdRef.current === editing.id && onCloseInitialQuote;
          rueckwegQuoteIdRef.current = null;
          setEditing(null);
          if (zurueck) onCloseInitialQuote();
        }}
        company={company}
        quoteConfig={quoteConfig}
        invoiceConfig={invoiceConfig}
        userProfile={userProfile}
        settings={settings}
        onOpenProject={onOpenProject}
        onDelete={() => setDeleteTarget(editing)}
        // Nachtrag aus dem Editor angelegt: in die Liste und direkt oeffnen.
        onCreatedQuote={(q) => { setQuotes((prev) => [q, ...prev]); setEditing(q); }}
      />
      {deleteDialog}
      </>
    );
  }

  return (
    <div ref={scrollRef} data-glass-view="quotes" className="h-full overflow-y-auto">
    {/* Volle Breite wie die Rechnungsansicht: kein max-w-Deckel mehr,
        damit Liste und Kacheln den Bildschirm ausnutzen. */}
    <div className="space-y-6 animate-fade-in p-4 md:p-6 pb-20">
      {/* Header */}
      <div data-glass="kopf" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col lg:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-4 w-full lg:w-auto">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
            <FileText className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Angebote</h2>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">{quotes.length} Angebote</p>
          </div>
        </div>
        <div className="flex gap-3 w-full lg:w-auto justify-end">
          <Button
            variant="secondary"
            onClick={() => { setImportError(null); setImportOpen(true); }}
            icon={<Upload className="w-4 h-4" />}
            title="Ein fertiges Angebot (Altbeleg oder aus einem Vorsystem) als PDF übernehmen"
          >
            Angebot einlesen
          </Button>
          <Button variant="primary" onClick={() => { setPickedCustomer(null); setPickerStep('customer'); setPickerOpen(true); }} className="shadow-lg border-b-2 border-blue-700" icon={<Plus className="w-4 h-4" />}>
            Neues Angebot
          </Button>
        </div>
      </div>

      {/* Kennzahlen */}
      {!loading && !error && (
        <QuoteKpiBar
          stats={stats}
          period={period}
          onPeriodChange={handlePeriodChange}
          onSelectStatus={setFilter}
          onFollowUpClick={showFollowUpTab}
        />
      )}

      {/* Reiter */}
      <div data-glass="reiter" className="flex gap-2 border-b border-slate-200">
        {([['quotes', 'Angebote'], ['followup', 'Nachfassen']] as ['quotes' | 'followup', string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`relative px-5 py-3 text-sm font-bold transition-all -mb-px border-b-2 ${
              tab === key ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <span className="flex items-center gap-2">
              {key === 'followup' && <Bell className="w-4 h-4" />}
              {label}
              {key === 'followup' && openFollowUpCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${dueFollowUpCount > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                  {dueFollowUpCount > 0 ? dueFollowUpCount : openFollowUpCount}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>

      {/* Reiter "Nachfassen" */}
      {tab === 'followup' && (
        loading ? (
          <div className="py-24 text-center text-slate-400 bg-white rounded-3xl border border-slate-200">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
            <p className="text-sm font-medium">Angebote werden geladen…</p>
          </div>
        ) : error ? (
          <div className="py-16 text-center bg-white rounded-3xl border border-red-100">
            <div className="bg-red-50 p-4 rounded-full w-fit mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <p className="font-bold text-slate-700">Fehler beim Laden</p>
            <p className="text-xs text-slate-500 mt-1 mb-4">{error}</p>
            <Button variant="secondary" onClick={load} icon={<RefreshCw className="w-4 h-4" />}>Erneut versuchen</Button>
          </div>
        ) : (
          <QuoteFollowUpTab
            quotes={quotes}
            todayIso={todayIso()}
            customerName={customerName}
            onDone={(q) => setFollowUpTarget(q)}
            onSnooze={handleSnooze}
            onOpen={(q) => setEditing(q)}
          />
        )
      )}

      {tab === 'quotes' && (
        <>
      {/* Filter + Suche */}
      <div data-glass="filterleiste" className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Suchen: Nummer, Kunde, Bezeichnung…"
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
          />
        </div>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
              filter === f.key ? 'bg-blue-600 text-white shadow' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Inhalt */}
      {loading ? (
        <div className="py-24 text-center text-slate-400 bg-white rounded-3xl border border-slate-200">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
          <p className="text-sm font-medium">Angebote werden geladen…</p>
        </div>
      ) : error ? (
        <div className="py-16 text-center bg-white rounded-3xl border border-red-100">
          <div className="bg-red-50 p-4 rounded-full w-fit mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <p className="font-bold text-slate-700">Fehler beim Laden</p>
          <p className="text-xs text-slate-500 mt-1 mb-4">{error}</p>
          <Button variant="secondary" onClick={load} icon={<RefreshCw className="w-4 h-4" />}>Erneut versuchen</Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-slate-400 bg-white rounded-3xl border-2 border-dashed border-slate-100">
          <div className="bg-slate-50 p-4 rounded-full w-fit mx-auto mb-4">
            <FileText className="w-10 h-10 opacity-20" />
          </div>
          <p className="font-bold">{filter === 'all' ? 'Noch keine Angebote' : 'Keine Angebote in diesem Status'}</p>
          <p className="text-xs">{filter === 'all' ? 'Legen Sie Ihr erstes Angebot an.' : 'Wählen Sie einen anderen Filter.'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[860px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                  <th className="px-5 py-4">Nummer</th>
                  <th className="px-5 py-4">Kunde</th>
                  <th className="px-5 py-4">Titel</th>
                  <th className="px-5 py-4 text-right">Netto</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Nachfassen</th>
                  <th className="px-3 py-4 text-right w-[60px]">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((q) => {
                  const eff = effectiveStatus(q);
                  return (
                    <tr key={q.id} onClick={() => setEditing(q)} className="hover:bg-blue-50/40 transition-colors group cursor-pointer">
                      <td className="px-5 py-4 align-middle font-bold text-slate-800 whitespace-nowrap">
                        {q.quoteNumber}
                        {/* Eingelesener Altbeleg: die Nummer im Original ist eine
                            andere — ohne Kennzeichnung sucht man sie vergeblich. */}
                        {isImportedQuote(q) && (
                          <span
                            className="ml-2 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 align-middle"
                            title={`Eingelesen aus einem Beleg${q.details?.importedQuote?.originalNumber ? ` (Original: ${q.details.importedQuote.originalNumber})` : ''}`}
                          >
                            Import
                          </span>
                        )}
                      </td>
                      {/* Umbrechen statt nowrap: sonst setzt der laengste
                          Kundenname die Spaltenbreite, die Tabelle wird breiter
                          als der Bildschirm und Status/Aktion liegen hinter
                          einem Querscroll am Tabellenende. */}
                      <td className="px-5 py-4 align-middle text-sm text-slate-600 min-w-[180px]">
                        <span className="flex items-center gap-1.5" title={customerName(q)}>
                          <Building2 className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                          <span className="line-clamp-2 break-words">{customerName(q)}</span>
                        </span>
                      </td>
                      <td className="px-5 py-4 align-middle text-sm text-slate-500 truncate max-w-[220px]">
                        {q.title || <span className="text-slate-300">—</span>}
                        {/* Nachtrag: zu welchem Projekt — der Titel allein
                            sagt nicht, dass kein neues Projekt entsteht. */}
                        {q.details?.nachtragZu && isSupplementQuote(q) && (
                          <div className="text-[11px] font-semibold text-indigo-600 truncate" title={supplementLabel(q.details.nachtragZu)}>
                            {supplementLabel(q.details.nachtragZu)}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 align-middle text-right text-sm font-bold text-slate-700 whitespace-nowrap">
                        {fmtEur(q.netTotal)}
                        {/* Monatsbetrag bzw. reines Einheitspreis-Angebot: der
                            Betrag ist kein Gesamtpreis. */}
                        {quoteAmountSuffix(q) && <span className="ml-1 text-[11px] font-medium text-slate-400">{quoteAmountSuffix(q)}</span>}
                        {/* Teilbeauftragt: wie viel davon beauftragt ist. */}
                        {eff === 'partial' && (
                          <div className="text-[11px] font-semibold text-teal-700">beauftragt {fmtEur(quoteOrderSummary(q).orderedNet)}</div>
                        )}
                      </td>
                      <td className="px-5 py-4 align-middle"><StatusBadge status={eff} /></td>
                      <td className="px-5 py-4 align-middle text-sm text-slate-500 whitespace-nowrap">{fmtDate(q.followUpDate)}</td>
                      <td className="px-3 py-4 align-middle text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); setReuseCustomer(null); setReuseTarget(q); }}
                            className="p-2 text-slate-300 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all"
                            title="Angebot wiederverwenden"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          {q.status === 'draft' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteTarget(q); }}
                              className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                              title="Angebot löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

        </>
      )}

      {/* Erledigtes Nachfassen erfassen */}
      {followUpTarget && (
        <QuoteFollowUpDoneModal
          quote={followUpTarget}
          customerName={customerName(followUpTarget)}
          actorName={actorName}
          actorId={userProfile?.id}
          defaultIntervalDays={quoteConfig?.followUpDays ?? 14}
          saving={savingFollowUp}
          onCancel={() => (savingFollowUp ? undefined : setFollowUpTarget(null))}
          onSubmit={handleFollowUpSubmit}
        />
      )}

      {/* Neues-Angebot-Modal (2 Schritte: Kunde -> Vorlage, beim Nachtrag
          Kunde -> Projekt) */}
      {pickerOpen && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-visible animate-scale-in">
            <div className="bg-slate-900 text-white p-6 flex justify-between items-center rounded-t-3xl">
              <div className="flex items-center gap-3">
                <div className="bg-blue-500/20 p-2 rounded-xl border border-blue-500/30"><FileText className="w-6 h-6 text-blue-400" /></div>
                <div>
                  <h3 className="font-bold text-lg">Neues Angebot</h3>
                  <p className="text-[11px] text-slate-300">
                    {pickerStep === 'customer' ? 'Schritt 1: Kunde' : pickerStep === 'template' ? 'Schritt 2: Vorlage' : 'Schritt 2: Projekt'}
                  </p>
                </div>
              </div>
              <button onClick={closePicker} className="hover:bg-white/10 p-2 rounded-full transition-all"><X className="w-5 h-5" /></button>
            </div>

            {pickerStep === 'customer' ? (
              <>
                <div className="p-8 space-y-4">
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Kunde *</label>
                    <CustomerPicker
                      customers={customers}
                      value={pickedCustomer?.id}
                      onSelect={setPickedCustomer}
                      placeholder="Kunde auswählen…"
                      onCreateNew={darf.kunden ? setNewCustomerName : undefined}
                    />
                    {darf.kunden && (
                      <button
                        type="button"
                        onClick={() => setNewCustomerName('')}
                        className="ml-1 flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                        Kunde noch nicht angelegt? Neuen Kunden anlegen
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Art</label>
                    <div className="grid grid-cols-2 gap-2">
                      {([
                        ['new', 'Neues Angebot', 'Mit oder ohne Vorlage'],
                        ['supplement', 'Nachtrag zu bestehendem Projekt', 'Erhöht bei Annahme das Projekt'],
                      ] as const).map(([key, label, hint]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setPickedKind(key)}
                          aria-pressed={pickedKind === key}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            pickedKind === key ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20' : 'border-slate-200 hover:border-blue-300'
                          }`}
                        >
                          <div className="font-bold text-sm text-slate-800 leading-tight">{label}</div>
                          <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {pickedKind === 'supplement' ? (
                    <p className="text-[11px] text-slate-400 ml-1">Nummernkreis und Texte richten sich nach dem Hauptangebot des Projekts.</p>
                  ) : (
                    <div className="space-y-2">
                      <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Typ (Nummernkreis)</label>
                      <select
                        value={pickedTypeDigit}
                        onChange={(e) => setPickedTypeDigit(e.target.value as TypeDigit)}
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                      >
                        <option value="1">1 – SiGeKo</option>
                        <option value="2">2 – SiFa</option>
                        <option value="3">3 – Sonstige</option>
                      </select>
                      <p className="text-[11px] text-slate-400 ml-1">Bestimmt die Angebotsnummer (A-JJMM<strong>T</strong>NNN) und ist danach nicht mehr änderbar.</p>
                    </div>
                  )}
                </div>
                <div className="p-6 bg-slate-50 border-t flex justify-end gap-3 rounded-b-3xl">
                  <Button variant="ghost" onClick={closePicker}>Abbrechen</Button>
                  <Button variant="primary" onClick={advanceFromCustomerStep} disabled={!pickedCustomer} className="shadow-lg border-b-2 border-blue-700">
                    Weiter
                  </Button>
                </div>
              </>
            ) : pickerStep === 'project' ? (
              <>
                <div className="p-6 space-y-3">
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Projekt wählen</label>
                  <div className="relative">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                    <input
                      autoFocus
                      value={projectQuery}
                      onChange={(e) => setProjectQuery(e.target.value)}
                      placeholder="Projektnummer oder -name…"
                      className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                    />
                    {projectsLoading && <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
                  </div>
                  <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                    {projectSearchError ? (
                      <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl p-3">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span>{projectSearchError}</span>
                      </div>
                    ) : projectOptions.length === 0 ? (
                      projectsLoading ? (
                        <div className="py-8 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                      ) : (
                        <p className="py-6 text-center text-sm text-slate-400">Keine Projekte gefunden.</p>
                      )
                    ) : (
                      projectOptions.map((p) => {
                        const pick = supplementPickState(p, pickedCustomer?.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => handleCreateSupplement(p)}
                            disabled={!pick.selectable || creating}
                            className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border text-left transition-all ${
                              pick.selectable
                                ? 'border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 disabled:opacity-60'
                                : 'border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed'
                            }`}
                          >
                            <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><FolderOpen className="w-4 h-4" /></div>
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-sm text-slate-800 truncate">
                                {p.projectNumber}{p.projectName ? ` ${p.projectName}` : ''}
                              </div>
                              {pick.note && (
                                <div className={`text-xs ${pick.blocked ? 'text-red-600' : 'text-slate-400'}`}>{pick.note}</div>
                              )}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                  {pickerError && (
                    <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl p-3">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      <span>{pickerError}</span>
                    </div>
                  )}
                </div>
                <div className="p-6 bg-slate-50 border-t flex justify-between gap-3 rounded-b-3xl">
                  <Button variant="ghost" onClick={() => { setPickerError(null); setPickerStep('customer'); }} disabled={creating}>Zurück</Button>
                  {creating && <span className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" />Wird angelegt…</span>}
                </div>
              </>
            ) : (
              <>
                <div className="p-6 space-y-2 max-h-[50vh] overflow-y-auto">
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Vorlage wählen</label>
                  {/* Ohne Vorlage */}
                  <button
                    type="button"
                    onClick={() => handleCreate(null)}
                    disabled={creating}
                    className="w-full flex items-center gap-3 p-3.5 rounded-2xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all text-left disabled:opacity-60"
                  >
                    <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><Plus className="w-4 h-4" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm text-slate-800">Ohne Vorlage</div>
                      <div className="text-xs text-slate-400">Leeres Angebot mit Standard-Sektionen</div>
                    </div>
                  </button>

                  {templatesLoading ? (
                    <div className="py-8 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                  ) : (
                    templates.map((t) => (
                      <div key={t.id} className="flex items-center gap-2 p-3.5 rounded-2xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all group">
                        <button type="button" onClick={() => handleCreate(t)} disabled={creating} className="flex-1 flex items-center gap-3 text-left min-w-0 disabled:opacity-60">
                          <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-500 flex items-center justify-center shrink-0"><Bookmark className="w-4 h-4" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm text-slate-800 truncate">{t.name}</div>
                            <div className="text-xs text-slate-400 flex items-center gap-1"><Layers className="w-3 h-3" />{(t.payload.items || []).length} Positionen</div>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteTemplate(t.id)}
                          disabled={deletingTemplateId === t.id}
                          className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all shrink-0"
                          title="Vorlage löschen"
                        >
                          {deletingTemplateId === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-6 bg-slate-50 border-t flex justify-between gap-3 rounded-b-3xl">
                  <Button variant="ghost" onClick={() => setPickerStep('customer')} disabled={creating}>Zurück</Button>
                  {creating && <span className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" />Wird angelegt…</span>}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Neuer Kunde aus Schritt 1 — liegt ueber dem Neues-Angebot-Modal. */}
      {pickerOpen && newCustomerName !== null && (
        <NewCustomerDialog
          initialName={newCustomerName}
          useExistingLabel="Übernehmen"
          useArchivedExisting={false}
          onCreated={takeNewCustomer}
          onUseExisting={takeNewCustomer}
          onClose={() => setNewCustomerName(null)}
        />
      )}

      {/* Angebot einlesen: fertigen Beleg als Angebotsdatensatz uebernehmen */}
      {importOpen && (
        <QuoteDocumentImportModal
          customers={customers}
          defaultTaxRate={quoteConfig?.defaultTaxRate ?? 19}
          busy={importing}
          error={importError}
          onClose={() => { if (!importing) { setImportOpen(false); setImportError(null); } }}
          onImport={handleImportQuote}
        />
      )}

      {/* Wiederverwenden: Kunde waehlen und Angebot duplizieren */}
      {reuseTarget && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-visible animate-scale-in">
            <div className="bg-slate-900 text-white p-6 flex justify-between items-center rounded-t-3xl">
              <div className="flex items-center gap-3">
                <div className="bg-blue-500/20 p-2 rounded-xl border border-blue-500/30"><Copy className="w-6 h-6 text-blue-400" /></div>
                <div>
                  <h3 className="font-bold text-lg">Wiederverwenden</h3>
                  <p className="text-[11px] text-slate-300">{reuseTarget.quoteNumber}</p>
                </div>
              </div>
              <button onClick={() => { setReuseTarget(null); setReuseCustomer(null); }} className="hover:bg-white/10 p-2 rounded-full transition-all"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-8 space-y-2">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Für welchen Kunden?</label>
              <CustomerPicker customers={customers} value={reuseCustomer?.id} onSelect={setReuseCustomer} placeholder="Kunde auswählen…" />
            </div>
            <div className="p-6 bg-slate-50 border-t flex justify-end gap-3 rounded-b-3xl">
              <Button variant="ghost" onClick={() => { setReuseTarget(null); setReuseCustomer(null); }}>Abbrechen</Button>
              <Button variant="primary" onClick={handleReuse} disabled={!reuseCustomer || duplicating} className="shadow-lg border-b-2 border-blue-700" icon={duplicating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}>
                Duplizieren
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Löschen-Dialog — als Variable oben, weil ihn auch der Editor
          aufruft und der Listen-Zweig dort gar nicht gerendert wird. */}
      {deleteDialog}
    </div>
    </div>
  );
};
